import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryZoneRow, GetDeliveryZonesParams, GetDeliveryZonesResponse } from '../types/delivery-zone.type';
import { ADMIN_DELIVERY_ZONES_API_PATHS } from '../constants/delivery-zones-api-paths.constants';

export type GetDeliveryZonesRequest = GetDeliveryZonesParams;

type UseAdminGetDeliveryZonesResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  deliveryZones: DeliveryZoneRow[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetDeliveryZonesResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  deliveryZones: [],
};

export function useAdminGetDeliveryZones({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetDeliveryZonesRequest): UseAdminGetDeliveryZonesResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<GetDeliveryZonesResponse>(
        ADMIN_DELIVERY_ZONES_API_PATHS.GET_DELIVERY_ZONES,
        { params: { page, limit, sortBy, sortDirection, filters: serializedFilters } }
      );

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        deliveryZones: response.deliveryZones,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la récupération des zones de livraison'
        ),
        { variant: 'error' }
      );
      setResult({ ...INITIAL_STATE, isLoading: false });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
